import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade } from '@mode/charts/contract';
import { ReportViewerFacade } from '@mode/report-viewer/domain';
import { ReportFacade } from '@mode/reports/domain';
import { DataViewTypes, FeatureFlagsFacade, ReportTypes } from '@mode/shared/contract-common';
import { ModalService } from '@mode/shared/ui-angularjs';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { DataViewFacade } from '@mode/switch-views/contract';
import type { StateService } from '@uirouter/angularjs';
import * as angular from 'angular';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { MixableBaseController } from '~/common/component';
import { isStandaloneVizPage } from '~/lib/router-helpers';
import { makeReportViewContainerObservables } from '~/observables/report-view-container.observables';
import { routerSelector } from '~/reducers/selectors';
import { ClientDataService } from '~/services/client-data-service';
import { AppStore } from '~/types/main.types';
import { ReportViewDataService } from '../../services';

class ReportViewContainer extends MixableBaseController implements angular.IOnInit {
  static $inject = [
    'ModalService',
    'RxStoreService',
    'ClientDataService',
    'ReportViewDataService',
    'ReportViewerFacade',
    'DataViewFacade',
    'ChartFacade',
    '$state',
    'AnalyticsService',
    '$stateParams',
    'ReportFacade',
    'FeatureFlagsFacade',
    'AccountFacade',
  ];

  reportIsShared = false;
  reportIsEmbedded = false;
  reportIsWhiteLabelEmbedded = false;

  pageContext: string;
  embedUrl: string;
  reportUrl: string;
  rootUrl: string;
  isParamContainerVisible: boolean;
  rowStyle$: Observable<string>;
  hidePlatformHeader$: Observable<boolean>;
  isDashboardState$: Observable<boolean>;
  hasRun$: Observable<boolean>;
  reportRunDetailsLink$: Observable<string>;
  reportRunErrorLink$: Observable<string>;
  lastSuccessfullyRunAt$: Observable<Date>;
  reportRunCompletedAt$: Observable<Date>;
  mostRecentReportRunLink$: Observable<string>;
  showParamsHeader$: Observable<boolean>;
  showFilterHeader$: Observable<boolean>;
  filterPanelOpen$: Observable<boolean>;
  showReportView$: Observable<boolean>;
  reportRunBannerType$: Observable<ReportTypes.ReportRunBannerType>;
  reportRunBannerType: ReportTypes.ReportRunBannerType;
  isLocal$: Observable<boolean>;
  private _reportHasRunErrors = false;
  private _isReportViewDashBoard = true;
  private _showLoadingSpinner$: Observable<boolean>;

  constructor(
    private modal: ModalService,
    private rxStore: AppStore,
    private clientDataService: ClientDataService,
    private reportViewDataService: ReportViewDataService,
    private reportViewerFacade: ReportViewerFacade,
    private dataViewFacade: DataViewFacade,
    private chartFacade: ChartFacade,
    private $state: StateService,
    private analyticsService: AnalyticsService,
    private $stateParams,
    private reportFacade: ReportFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private accountFacade: AccountFacade
  ) {
    super();
  }

  $onInit(): void {
    this.loadHeaderReportData();
    const observables = makeReportViewContainerObservables(
      this.reportViewerFacade,
      this.$state,
      this.rxStore,
      this.chartFacade,
      this.dataViewFacade
    );
    this.rootUrl = `${window.location.origin}/`;
    this.showFilterHeader$ = observables.showFilterHeader$;
    this.showParamsHeader$ = observables.showParamsHeader$;
    const webLink$ = observables.webLink$;
    this.reportRunDetailsLink$ = observables.reportRunDetailsLink$;
    this.reportRunErrorLink$ = observables.reportRunErrorLink$;
    this.mostRecentReportRunLink$ = observables.mostRecentReportRunLink$;
    this.rowStyle$ = observables.rowStyle$;
    this.hasRun$ = observables.hasRun$;
    this.lastSuccessfullyRunAt$ = observables.lastSuccessfullyRunAt$;
    this.reportRunCompletedAt$ = observables.reportRunCompletedAt$;
    this.reportRunBannerType$ = observables.reportRunBannerType$;
    this.isLocal$ = observables.isLocal$;
    this.analyticsService.track(AnalyticsEvent.Name.UserExperienceMilestone, {
      user_experience_id: this.analyticsService.userExperienceId,
      location: AnalyticsEvent.SubContext.ReportView,
      context: AnalyticsEvent.Context.Report,
      context_id: this.$stateParams['reportId'],
      interaction: AnalyticsEvent.Interaction.LoadingIndicator,
    });

    this.rxStore
      .select(routerSelector)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((routerState) => {
        this._isReportViewDashBoard =
          routerState.name === 'reportShow.noRuns.dashboard' || routerState.name === 'reportShow.runs.dashboard';
      });

    this._showLoadingSpinner$ = combineLatest([
      this.reportViewerFacade.hasRun$,
      this.reportViewerFacade.iframeLoaded$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([hasRun, iframeLoaded]) => {
        return !hasRun || !iframeLoaded;
      })
    );

    this.reportViewDataService.report$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((report) => (this.reportIsWhiteLabelEmbedded = report?.is_signed));

    combineLatest([this.reportViewerFacade.reportRunState$, this.reportViewerFacade.token$])
      .pipe(
        filter(([, reportToken]) => reportToken !== ''),
        takeUntil(this.destroyed$)
      )
      .subscribe(([reportRunStatus, reportToken]) => {
        const runCompleted = [
          DataViewTypes.ReportRunStatus.Cancelled,
          DataViewTypes.ReportRunStatus.Completed,
          DataViewTypes.ReportRunStatus.Succeeded,
          DataViewTypes.ReportRunStatus.Failed,
        ].includes(reportRunStatus);
        this._reportHasRunErrors = reportRunStatus === 'cancelled' || reportRunStatus === 'failed';
        this.analyticsService.track(AnalyticsEvent.Name.UserExperienceStarted, {
          user_experience_id: this.analyticsService.userExperienceId,
          location: AnalyticsEvent.SubContext.ReportView,
          context: AnalyticsEvent.Context.Report,
          context_id: reportToken,
          run_completed: runCompleted,
        });
      });

    this.showReportView$ = this.reportViewerFacade.isReportBuilderEmpty$.pipe(
      map((isReportBuilderEmpty) => {
        return this._isReportViewDashBoard ? isReportBuilderEmpty : this._isReportViewDashBoard;
      })
    );

    this.filterPanelOpen$ = this.rxStore.select('transient', 'editor', 'isFilterPanelOpen');

    this.reportRunBannerType$.pipe(takeUntil(this.destroyed$)).subscribe((banner) => {
      this.reportRunBannerType = banner;
    });

    // Initialize the state of the params form field component
    this.showParamsHeader$
      .pipe(
        takeUntil(this.destroyed$),
        filter((showParamsHeader) => showParamsHeader != null),
        first(),
        map((showParamsHeader) => {
          this.isParamContainerVisible = showParamsHeader;
        })
      )
      .subscribe();

    // Build urls for share modal
    webLink$
      .pipe(
        takeUntil(this.destroyed$),
        map((webLink) => {
          this.reportUrl = webLink;
          this.embedUrl = `${webLink}/embed`;
        })
      )
      .subscribe();

    // These are independent of GF or NON-GF, so for clarity, they can remain here temporarily.
    this.hidePlatformHeader$ = this.rxStore
      .select(routerSelector)
      .pipe(map((routerState) => isStandaloneVizPage(routerState.name)));

    this.isDashboardState$ = this.rxStore
      .select(routerSelector)
      .pipe(map(({ name }) => name === 'reportShow.runs.dashboard' || name === 'reportShow.noRuns.dashboard'));
  }

  shareSimpleModal() {
    if (this.embedUrl && this.reportUrl && this.rootUrl) {
      this.modal.open(
        {
          reportLink: this.embedUrl,
          webLink: this.reportUrl,
          embedLink: `${this.rootUrl}embed.js`,
        },
        {
          template: `
            <div class="default-modal">
              <h3 class="modal-header">Share Report<a href="#" class="modal-close icon-close" ng-click="$dismiss();"></a></h3>
              <div mode-community-share report-link="reportLink" web-url="webLink" embed-link="embedLink"></div>
              <div class="modal-action-bar">
                <button class="btn btn-lg btn-filled-primary" ng-click="$dismiss()">Done</button>
              </div>
            </div>
          `,
        }
      );
    }
  }

  showRunBanner() {
    return this.reportRunBannerType !== ReportTypes.ReportRunBannerTypes.NoBanner;
  }

  toggleParams() {
    this.isParamContainerVisible = !this.isParamContainerVisible;
  }

  loadHeaderReportData() {
    this.accountFacade.isLoggedIn$.pipe(takeUntil(this.destroyed$)).subscribe((isLoggedIn) => {
      const reportToken = this.$state.params['reportId'];
      if (isLoggedIn) {
        this.reportFacade.loadReportViewContainerHeaderData(reportToken);
      } else {
        this.reportFacade.loadRestrictedReportViewContainerHeaderData();
      }
    });
  }
}

export const ID = 'reportViewContainer';

export const reportViewContainerComponent: angular.IComponentOptions = {
  template: `
  <div class="report-view" ng-class="ctrl.rowStyle$ | async:this">
    <platform-header
      header-style="Dark"
      menu-type="Report"
      is-local="ctrl.isLocal$ | async: this"
      on-simple-share="ctrl.shareSimpleModal()"
      page-context="ctrl.pageContext">
    </platform-header>
    <sharing-banner
      ng-if="ctrl.isSharingBannerEnabled"
      report-is-embedded="ctrl.reportIsEmbedded"
      report-is-shared="ctrl.reportIsShared"
      report-is-white-label-embedded="ctrl.reportIsWhiteLabelEmbedded"
      on-dismiss="ctrl.dismissSharingBanner()">
    </sharing-banner>
    <div
      ng-if="ctrl.reportViewDataService.errorNoToken && !(ctrl.hasRun$ | async: this)"
      class="alert alert-no-report-run"
    >This report has not been run yet
    </div>
    <report-params-container
      ng-if ="(ctrl.showParamsHeader$ | async:this) || (ctrl.showFilterHeader$ | async:this)"
      show-params-header="ctrl.showParamsHeader$ | async:this"
      show-filter-header="ctrl.showFilterHeader$ | async:this"
      is-param-container-visible="ctrl.isParamContainerVisible"
      on-toggle="ctrl.toggleParams()"
    ></report-params-container>
    <div
      class="report-view-container"
      ng-class="{
        'has-explore-header': (ctrl.hidePlatformHeader$ | async:this),
        'has-subheader': (ctrl.isDashboardState$ | async:this) && ((ctrl.showParamsHeader$ | async:this) || (ctrl.showFilterHeader$ | async:this)),
        'subheader-collapsed': (ctrl.hidePlatformHeader$ | async:this) || !(ctrl.isDashboardState$ | async:this) || !ctrl.isParamContainerVisible
      }"
      ng-if="ctrl.hasRun$ | async:this">
      <mode-report-run-banner
        ng-if="(ctrl.reportRunBannerType$ | async:this) !== null"
        [banner-type]="ctrl.reportRunBannerType$ | async:this"
        [last-successfully-run-at]="ctrl.lastSuccessfullyRunAt$ | async:this"
        [run-completed-at]="ctrl.reportRunCompletedAt$ | async:this"
        [report-link]="ctrl.reportUrl"
        [report-run-details-link]="ctrl.reportRunDetailsLink$ | async:this"
        [report-run-error-link]="ctrl.reportRunErrorLink$ | async:this"
        [most-recent-report-run-link]="ctrl.mostRecentReportRunLink$ | async:this"
      ></mode-report-run-banner>
      <div
        ng-class="{'results-content': true, 'show-banner': ctrl.showRunBanner(),
        'white-bg':(ctrl.showReportView$ | async: this)}"
        ui-view
      >
      </div>
    </div>
    <mode-loader
      ng-if="ctrl._isReportViewDashBoard && ctrl._showLoadingSpinner$ | async:this"
      ng-class="{'hide': ctrl.reportHasRunErrors || ctrl.reportIsWhiteLabelEmbedded}"
    ></mode-loader>
  </div>
  <view-data-lightbox-modal
    read-only="true"
    container="body"
  ></view-data-lightbox-modal>
  `,
  controller: ReportViewContainer,
  controllerAs: 'ctrl',
  bindings: {
    pageContext: '<?',
  },
};
